import React, { useState, useEffect, useRef } from "react";
import lottie from "lottie-web";
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import TYPE from "../../constant/TYPE";
import useLoading from "../../hooks/useLoading";
import useSound from "../../hooks/useSound";
import createTimer from "../../utils/createTimer";
import { getParam } from "../../utils/utilityEtc";
import Button from "./Button";
import useAmplitude from "../../hooks/useAmplitude";
import { useAppBackBtn } from "../../hooks";
import { appsflyer, appsflyerEventName } from "../../utils/appsflyer";
import { mathpidLogAPICall } from "../../redux/logData";

/*
구성 순서 --> play:replay:stop
패드방식(v-tab) :  
	Play시 pause버튼  보임			  : TFF	 /FFT
	Play시 pause버튼 안보임    		: TFF	 /FFF
	Pause시                      : FFT	/TFF
	완료시						           : FTF	/FTF

PC방식(v-pc) :
	버튼보여줌 여부는  : pchover로 제어됨
	Play시 						            : TFF	/TFF(변경없음)	
	Pause시                       : FFT	/TFF(변경없음)
	완료시						            : FTF	/TFF(변경없음)
*/

const ConceptCheckPlayer = (props) => {

  const location = useLocation();
  const dispatch = useDispatch();
  const { setAmplitudeLog } = useAmplitude();
  const { setBackEventData } = useAppBackBtn();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [currentFrame, setCurrentFrame] = useState(1);
  const [totalFrame, setTotalFrame] = useState(1);
  const [pchover, setPchover] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [aniData, setAniData] = useState({ url: '', title: '' });
  const [amplitudeEvent, setAmplitudeEvent] = useState(null);

  const containerRef = useRef();
  const unitFrames = useRef(0);
  const totalFrames = useRef(0);
  const dataButton = useRef({});
  const runningTime = useRef(0);
  const animation = useRef();

  const deviceInfo = useSelector( state => state.deviceInfo );
  const conceptPopupInfo = useSelector(state => state.common.conceptCheckPlayer);

  const { t } = useTranslation();

  const { setLoading, clearLoading } = useLoading();
  const { setSound, playSound, clearSound } = useSound();

  const [parentState, setParentState] = useState({
    play: true,
    stop: false,
    replay: false,
  });

  const [buttonState, setButtonState] = useState({
    play: false,
    stop: false,
    replay: false,
  });

  useEffect(() => {
    if (location.search) {
      const { search } = location;
      const title = decodeURI(getParam(search, 'title'));
      const url = getParam(search, 'url');

      if (title && url) setAniData({ title, url });
    } else {
      setAniData({ title: props.title, url: props.url });
    }
  }, [props, location]);

  const mount = () => {
    const updateTime = ( time ) => {
      runningTime.current = time;
    };
    const timer = createTimer( updateTime );
    timer.start();
    setSound( { src : `${ process.env.PUBLIC_URL }/static/audio/lc_05_02_open.ogg` } );
    playSound();
    return () => {
      timer.clear();
      clearSound();
    };
  };

  const updateUrl = () => {
    setLoading({ isShowSpinner: true, spinnerType: '1' });
    if ( aniData.url ) {
      let total = 0;
      let tempPage = 0;
      const initialize = () => {
        const lowerFilename = aniData.url.toLowerCase().replace(".json", "");
        const unitTime = parseInt(
          lowerFilename.substr(lowerFilename.lastIndexOf("_") + 1)
        );
        unitFrames.current = unitTime * animation.current.frameRate;
        totalFrames.current = animation.current.totalFrames;
        //setTotalPage(Math.floor(animation.current.totalFrames / unitFrames.current));
        total = Math.ceil(animation.current.totalFrames / unitFrames.current);
        setTotalPage(total);
        setTotalFrame(animation.current.totalFrames);

        dispatch(mathpidLogAPICall({
          verbId: 'played',
          objectId: 'video',
          idName: 'CONTENTS_CODE',
          idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
        }));

        animation.current.play();
        animation.current.addEventListener("enterFrame", animationEnterFrame);
      };
      const animationEnterFrame = () => {
        tempPage = Math.floor(animation.current.currentFrame / unitFrames.current) + 1;
        setCurrentPage(tempPage);
        setCurrentFrame(Math.floor(animation.current.currentFrame));
        if (animation.current.currentFrame + 1 === totalFrames.current) {
          setParentState({ play: false, stop: false, replay: true });
          setIsFinished(true);
          if (deviceInfo.type === TYPE.DEVICE.PC) setPchover(true);
          else setButtonState({ play: false, stop: false, replay: true });
        }
      };
      animation.current = lottie.loadAnimation({
        container: containerRef.current,
        path: aniData.url,
        renderer: "svg",
        loop: false,
        autoplay: false,
      });
      // animation lottie가 에러나면 로딩바가 계속 남아서 아무것도 못함.
      // 방어 코드로 그냥 clearLoading 삽입... 답이 없음..(리펙토링 필요)
      clearLoading();
      animation.current.addEventListener("DOMLoaded", () => {
        // clearLoading();
        initialize();
        const event = {
          "소스화면": '/learning/result',
          "소주제이름": aniData?.title,
          "소주제코드": location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
          "컨텐츠 길이": total,
          "커리큘럼그룹": conceptPopupInfo?.thmaNm,
          "커리큘럼명": conceptPopupInfo?.stepNm,
        }
        setAmplitudeEvent(event);
        setAmplitudeLog({
          eventName: {
            name: '개념영상 시작',
            event
          },
          isHackleSend: true,
        });

        appsflyer({
          eventName: appsflyerEventName.af_content_view,
          eventParams: {
            af_content_type: conceptPopupInfo?.thmaNm,
            af_content_id: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
          }
        });

        dispatch(mathpidLogAPICall({
          verbId: 'initialized',
          objectId: 'video',
          idName: 'CONTENTS_CODE',
          idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
        }));

        dispatch(mathpidLogAPICall({
          verbId: 'initialized',
          objectId: 'concept',
          idName: 'CONTENTS_CODE',
          idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
        }));

      });

      return () => {
        setAmplitudeLog({
          eventName: {
            name: '개념영상 종료',
            event: {
              "소주제이름": aniData?.title,
              "소주제코드": location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
              "컨텐츠 길이": total,
              "학습종료위치": tempPage,
              "진행 percentage": String(Math.ceil((tempPage) / Number(total) * 100)),
              "커리큘럼그룹": conceptPopupInfo?.thmaNm,
              "커리큘럼명": conceptPopupInfo?.stepNm,
            }
          }
        })
        dispatch(mathpidLogAPICall({
          verbId: 'terminated',
          objectId: 'video',
          idName: 'CONTENTS_CODE',
          idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
        }));

        dispatch(mathpidLogAPICall({
          verbId: 'terminated',
          objectId: 'concept',
          idName: 'CONTENTS_CODE',
          idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
        }));

        animation.current.removeEventListener("DOMLoaded", initialize);
        animation.current.removeEventListener("enterFrame", animationEnterFrame);
        animation.current.destroy();
      };
    }
  };

  const updateButtonState = () => {
    dataButton.current = buttonState;
  };

  useEffect(() => {
    mount();
    setBackEventData({ eventFunc: () => hide() })
  }, []);

  useEffect(() => {
    if (totalPage !== 0 && currentPage === totalPage) {

      dispatch(mathpidLogAPICall({
        verbId: 'completed',
        objectId: 'video',
        idName: 'CONTENTS_CODE',
        idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
      }));

      dispatch(mathpidLogAPICall({
        verbId: 'completed',
        objectId: 'concept',
        idName: 'CONTENTS_CODE',
        idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
      }));
    }
  }, [currentPage])

  useEffect(updateUrl, [aniData.url]);
  useEffect(updateButtonState, [buttonState]);

  const showPlayLook = () => {
    setParentState({ play: true, replay: false, stop: false });
    if (deviceInfo.type !== TYPE.DEVICE.PC) {
      setButtonState({ play: false, replay: false, stop: true });
      setTimeout(function () {
        setButtonState({ ...dataButton.current, stop: false });
      }, 1000);
    }
  };

  const handlePrev = () => {
    if(currentPage === totalPage && totalFrame === currentFrame){
      setButtonState({ play: false, replay: true, stop: false });
    }else{
      setButtonState({ play: false, replay: false, stop: false });
    }
    playButtonSound();
    if (currentPage <= 1) return;
    setAmplitudeLog({
      eventName: {
        name: '개념영상 좌로가기',
        event: amplitudeEvent
      }
    })
    dispatch(mathpidLogAPICall({
      verbId: 'seeked',
      objectId: 'video',
      idName: 'CONTENTS_CODE',
      idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
    }));
    animation.current.goToAndPlay((currentPage - 1 - 1) * unitFrames.current, true);
  };

  const handleNext = () => {
    if(currentPage === totalPage && totalFrame === currentFrame){
      setButtonState({ play: false, replay: true, stop: false });
    }else{
      setButtonState({ play: false, replay: false, stop: false });
    }
    playButtonSound();
    if (currentPage >= totalPage) return;
    setAmplitudeLog({
      eventName: {
        name: '개념영상 우로가기',
        event: amplitudeEvent
      }
    })
    dispatch(mathpidLogAPICall({
      verbId: 'seeked',
      objectId: 'video',
      idName: 'CONTENTS_CODE',
      idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
    }));
    animation.current.goToAndPlay((currentPage - 1 + 1) * unitFrames.current, true);
  };

  const playButtonSound = () => {
    setSound( { src : `${ process.env.PUBLIC_URL }/static/audio/common_button.ogg` } );
    playSound();
  };

  const handlePause = (e) => {
    animation.current.pause();
    setParentState({ play: false, replay: false, stop: true });
    setButtonState({ play: true, replay: false, stop: false });
    setAmplitudeLog({
      eventName: {
        name: '개념영상 일시정지',
        event: amplitudeEvent
      }
    })
    dispatch(mathpidLogAPICall({
      verbId: 'paused',
      objectId: 'video',
      idName: 'CONTENTS_CODE',
      idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
    }));
    e.stopPropagation();
  };

  const handlePlay = (e) => {
    animation.current.play();
    showPlayLook();
    setAmplitudeLog({
      eventName: {
        name: '개념영상 다시시작',
        event: amplitudeEvent
      }
    })

    dispatch(mathpidLogAPICall({
      verbId: 'played',
      objectId: 'video',
      idName: 'CONTENTS_CODE',
      idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
    }));

    e.stopPropagation();
  };

  const handleRestart = (e) => {
    dispatch(mathpidLogAPICall({
      verbId: 'played',
      objectId: 'video',
      idName: 'CONTENTS_CODE',
      idValue: location.state?.stgCd ? location.state?.stgCd : conceptPopupInfo?.stgCd,
    }));

    
    animation.current.goToAndPlay(0, true);
    showPlayLook();
    e.stopPropagation();
  };

  const handleParentClick = () => {
    if (deviceInfo.type === TYPE.DEVICE.PC) return;
    if (parentState.play)
      setButtonState({ play: false, replay: false, stop: true });
    if (parentState.stop){
      setButtonState({ play: true, replay: false, stop: false });
    }
    if (parentState.replay)
      setButtonState({ play: false, replay: true, stop: false });
    setTimeout(function () {
      setButtonState({ ...dataButton.current, stop: false });
    }, 1000);
  };

  const handleParentMouseEnter = () => {
    if (deviceInfo.type !== TYPE.DEVICE.PC) return;
    setPchover(true);
  };

  const handleParentMouseLeave = () => {
    if (deviceInfo.type !== TYPE.DEVICE.PC) return;
    setPchover(false);
  };

  const getParentStyle = () => {
    let returnStyle = "ani-btnBox ";
    if (parentState.play) returnStyle += "playing ";
    if (parentState.stop) returnStyle += "stopped ";
    if (parentState.replay) returnStyle += "ended ";
    if (deviceInfo.type === TYPE.DEVICE.PC && pchover)
      returnStyle += " pc-hover";
    return returnStyle;
  };

  const getButtonStyle = (buttonName) => {
    if (buttonName === "btn-play")
      return buttonState.play
        ? "btn-lc-ani btn-play on"
        : "btn-lc-ani btn-play";
    if (buttonName === "btn-stop")
      return buttonState.stop
        ? "btn-lc-ani btn-stop on"
        : "btn-lc-ani btn-stop";
    if (buttonName === "btn-replay")
      return buttonState.replay
        ? "btn-lc-ani btn-replay on"
        : "btn-lc-ani btn-replay";
  };

  const hide = () => {
    props.onHide(runningTime.current, isFinished);
    window.parent.postMessage({ ccpData: 'close' }, '*');
  };

  return (
    <div className="lp-wrap lp-leargin_concept">
      <div className="x_btn_cont">
        <button className="btn1" onClick={ hide }><img width="40" src={`${ process.env.PUBLIC_URL }/static/images/icon/ic_popup_close@2x.png`} alt="" /></button>
      </div>
      <div className="lp-container lc-wrap">
        <div className="lc-cont">
          <div className="lcCont-inner">
            <div className="lc-header">
              <div className="tit1">{ t( 'LC_05_02_concept.msg001' ) }</div>
              <h1 className="tit">{aniData.title}</h1>
              <div className="bar_cont">
                <div className="bar3">
                  <div className="num"><span>{currentPage}</span> / {totalPage}</div>
                  <div className="bar_bg">
                    <div
                      className="bar"
                      style={{
                        width: totalFrame > 1?`${(100 * currentFrame) / totalFrame}%` : `0`,
                      }} />
                    </div>
                  </div>
              </div>
            </div>
            <div className="lc-body">
              <div className="aniBox">
                <div className="ani-wrap" ref={containerRef} id="aniBox" />
                <div
                  className={getParentStyle()}
                  onClick={handleParentClick}
                  onMouseEnter={handleParentMouseEnter}
                  onMouseLeave={handleParentMouseLeave}>
                  <Button
                    className={getButtonStyle("btn-play")}
                    onClick={handlePlay}>
                    재생
                  </Button>
                  <Button
                    className={getButtonStyle("btn-stop")}
                    onClick={handlePause}>
                    정지
                  </Button>
                  <Button
                    className={getButtonStyle("btn-replay")}
                    onClick={handleRestart}>
                    다시 재생
                  </Button>
                </div>
              </div>
              <Button className="btn-lc prev" onClick={handlePrev}>
                이전 설명
              </Button>
              <Button className="btn-lc next" onClick={handleNext}>
                다음 설명
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};

ConceptCheckPlayer.defaultProps = {
  title: '',
  url: '',
  onHide: () => {}
};

export default ConceptCheckPlayer;
